/**
 * Money rounding — the ONE implementation.
 *
 * `Math.round(n * 100) / 100` was copy-pasted privately into at least five
 * services (taxScenario, cashFlowForecast, checkSplit, menuProfitability,
 * VisualCrossing). They happened to agree, which is luck rather than design:
 * the moment one of them needs banker's rounding, or a different precision, or
 * a negative-zero guard, the others silently keep the old behaviour. That is
 * the same drift that has bitten this codebase repeatedly.
 */

/**
 * Round a currency amount to cents.
 *
 * Use this anywhere a money value is STORED or COMPARED. Production stored an
 * order with `tax: 3.0134999999999996` and `total: 44.0135` while its check and
 * payment carried `3.01` / `44.01` — a $0.0035 gap that makes an order look
 * underpaid against a payment that settled it in full.
 *
 * NOT for intermediate arithmetic: round once at the boundary, not at every
 * step, or the roundings compound. Proportional splits (tax across lines,
 * discounts across checks) should allocate in integer cents with the remainder
 * on the last element instead — see `allocateTaxToCheckItems`.
 */
export function roundToCents(value: number): number {
  if (!Number.isFinite(value)) return 0;
  // `+ 0` normalises -0 to 0, so a rounded-away negative never serialises as
  // "-0" into a money field.
  return Math.round(value * 100) / 100 + 0;
}

/** Whole cents as an integer — for ledger/provider payloads that demand minor units. */
export function toCents(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100);
}

/**
 * A money amount a PERSON TYPED → whole cents, or null when the field is blank
 * or not an amount. THE rule is the till's `cashTipsCentsFromText` (order-up-pos
 * `shift_review_acknowledge_cubit.dart`): read from the digits, never through a
 * float, so "0.29" is 29 cents and never 28; at most two decimals, because a
 * third is a typo to correct, not a rounding to invent; never negative.
 *
 * It also takes what a browser field receives and the till's keypad cannot
 * type: a leading `$`, surrounding spaces, and a decimal COMMA ("12,50") — the
 * decimal key of a phone set to a comma-decimal language types one. A comma is
 * never a thousands separator, so "1,250" is refused rather than guessed as
 * $1,250.00 or $1.25.
 *
 * Not `parseMoneyToCents` (posImport), which reads EXPORT cells and maps a blank
 * to 0: in a form, blank is "not entered yet", never $0.00.
 */
export function centsFromMoneyText(text: string): number | null {
  // Whitespace is trimmed, never matched: `\s*` on both sides of optional parts
  // backtracks cubically on a long run of spaces (CodeQL js/polynomial-redos).
  let amount = text.trim();
  if (amount.startsWith('$')) amount = amount.slice(1).trimStart();
  const match = /^(\d*)(?:[.,](\d{0,2}))?$/.exec(amount);
  if (!match) return null;
  const whole = match[1] ?? '';
  const fraction = match[2] ?? '';
  if (whole === '' && fraction === '') return null;
  const cents = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/**
 * Percent precision a preset may carry: 4 decimal places (17.5, 19.9, 12.3456).
 * A percent with MORE decimal places is first rounded to 4 (12.34567 → 12.3457).
 */
const PERCENT_SCALE = 10_000n;
/** 100 (percent → fraction) × PERCENT_SCALE. */
const PERCENT_DIVISOR = 100n * PERCENT_SCALE;

/**
 * The tip, in WHOLE CENTS, that a `percent` preset (percentage points: `17.5`
 * = 17.5%) offers on `baseCents` — rounded half away from zero at the cent.
 *
 * THE rule is the till's: order-up-pos `percentageTipCents` →
 * `CentAllocation.toCents` (`lib/utils/payments/tip_suggestions.dart`), which
 * every on-screen tip tile and printed slip there calls. Any surface that
 * offers, prints or charges a percentage tip calls THIS, so the receipt, the
 * web checkout and the till cannot offer different amounts for the same bill
 * (docs/CARD_TIPPING_ARCHITECTURE.md). Parity is held by
 * `__fixtures__/percentageTipCents.cases.json`, generated from the till rule.
 *
 * Computed in exact integer arithmetic, not `Math.round(base * pct / 100)`:
 * 1500 × 19.9 is 29849.999… in binary, which rounded to 298¢ on the receipt
 * while the till charged 299¢. A non-positive base or preset is no tip (0),
 * matching the till — a comped check offers nothing, never a negative tip.
 * Percents are rounded to 4 decimal places before pricing (see
 * PERCENT_SCALE); a non-finite or non-number percent throws a TypeError.
 */
export function percentageTipCents(baseCents: number, percent: number): number {
  // A preset that is not a finite number is a caller defect (an unparsed input,
  // a missing config value). Pricing it as "no tip" would print or charge 0¢
  // with nothing red, so it is refused loudly instead.
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    throw new TypeError(`percentageTipCents: percent must be a finite number, got ${String(percent)}`);
  }
  if (!Number.isFinite(baseCents)) return 0;
  const base = Math.round(baseCents);
  const scaledPercent = Math.round(percent * Number(PERCENT_SCALE));
  if (base <= 0 || scaledPercent <= 0) return 0;
  const product = BigInt(base) * BigInt(scaledPercent);
  // Both operands are positive, so half-up IS half away from zero here.
  return Number((product * 2n + PERCENT_DIVISOR) / (PERCENT_DIVISOR * 2n));
}
